import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Autocomplete, TextField } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import services from '../../services'
import { useAuth } from '../../hooks/useAuth'
import { useNotification } from '../../hooks/useNotification.js'
import NovoDashboard from './NovoDashboard'
import './gestao.scss'

const POR_PAGINA = 100

/** O que o catálogo precisa de cada dashboard (o endpoint leve não traz grupos, filtros nem acessos). */
interface ItemCatalogo { id: number; nome: string; tipo: 'powerbi' | 'html'; ativo: boolean; temDataset: boolean; ambientes: number[] }

/** Gestão › Dashboards: o catálogo. Cada dashboard pode aparecer em vários ambientes. */
export default function Catalogo() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { notify } = useNotification()
  const { getUserRoles } = useAuth()
  const admin = ((getUserRoles() || []) as string[]).includes('bi_dashboards_read_any')
  const [preenchendo, setPreenchendo] = useState<string | null>(null)
  const [lista, setLista] = useState<ItemCatalogo[] | null>(null)
  const [ambientes, setAmbientes] = useState<{ id: number; nome: string }[]>([])
  const [ambiente, setAmbiente] = useState<number | null>(Number(params.get('ambiente')) || null)
  const [erro, setErro] = useState<string | null>(null)
  const [busca, setBusca] = useState('')
  const [tipo, setTipo] = useState('')
  const [situacao, setSituacao] = useState('ativo')
  const [dataset, setDataset] = useState('')
  const [novo, setNovo] = useState(false)
  const [pagina, setPagina] = useState(1)

  const carregar = () => services.api.novoPortalCatalogo().then((r: any) => {
    if (r.fail) setErro(r.error || 'Não foi possível carregar o catálogo.')
    else setLista(r.data || [])
  })

  useEffect(() => {
    carregar()
    services.api.getEnvironmentsAll().then((r: any) => {
      if (!r.fail) setAmbientes((r.data || []).map((a: any) => ({ id: a.id, nome: a.name })).sort((a: any, b: any) => a.nome.localeCompare(b.nome)))
    })
  }, [])

  const filtrada = useMemo(() => {
    const q = busca.trim().toLowerCase()
    return (lista || [])
      .filter(d => !ambiente || d.ambientes.includes(ambiente))
      .filter(d => !q || d.nome.toLowerCase().includes(q))
      .filter(d => !tipo || d.tipo === tipo)
      .filter(d => !situacao || (situacao === 'ativo') === d.ativo)
      .filter(d => !dataset || (dataset === 'com') === (d.tipo === 'html' || d.temDataset))
  }, [lista, busca, tipo, situacao, ambiente, dataset])

  useEffect(() => { setPagina(1) }, [busca, tipo, situacao, ambiente, dataset])
  const visiveis = filtrada.slice(0, pagina * POR_PAGINA)
  // Só os ativos: os inativos sem dataset foram desligados de propósito, e preencher por
  // eles chamaria o Power BI à toa.
  const semDataset = (lista || []).filter(d => d.ativo && d.tipo !== 'html' && !d.temDataset).length

  // Um lote de workspaces por chamada, até acabar: milhares de relatórios não cabem numa requisição só.
  const preencherDatasets = async () => {
    let depoisDe: string | null = null, gravados = 0
    const faltam: string[] = []
    setPreenchendo('Preenchendo…')
    do {
      const r: any = await services.api.novoPortalPreencherDatasets(depoisDe)
      if (r.fail) { notify(`${r.error || 'Não foi possível preencher os datasets.'} ${gravados} já gravados; dá para continuar clicando de novo.`, { variant: 'error' }); break }
      gravados += r.data.preenchidos
      faltam.push(...(r.data.naoAchados || []))
      depoisDe = r.data.proximo
      setPreenchendo(`${gravados} gravados…`)
      if (!depoisDe) notify(`${gravados} relatório(s) com dataset gravado.${faltam.length ? ` ${faltam.length} não achados no Power BI (relatório apagado ou workspace sem acesso).` : ''}`, { variant: faltam.length ? 'warning' : 'success' })
    } while (depoisDe)
    setPreenchendo(null)
    carregar()
  }

  return (
    <div className="ng">
      <div className="ng-cab">
        <h1>Dashboards</h1>
        <div className="ng-acoes"><button type="button" className="ng-btn prim" onClick={() => setNovo(true)}><AddIcon />Novo dashboard</button></div>
      </div>
      <p className="ng-explic">Cada dashboard pode aparecer em vários ambientes. Abra um para ver as configurações gerais e onde ele aparece.</p>
      {admin && semDataset > 0 && (
        <div className="ng-aviso">
          <span><b>{semDataset} relatório(s) Power BI</b> ainda sem dataset gravado. O portal pode gravar o dataset que cada relatório usa.</span>
          <button type="button" className="ng-btn" disabled={!!preenchendo} onClick={preencherDatasets}>{preenchendo || 'Preencher pelo relatório'}</button>
        </div>
      )}

      <div className="ng-filtros">
        <label className="ng-busca"><SearchIcon fontSize="small" />
          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar dashboard" aria-label="Buscar dashboard" />
        </label>
        <Autocomplete
          className="ng-escolha" size="small" options={ambientes} value={ambientes.find(a => a.id === ambiente) || null}
          getOptionLabel={a => a.nome} isOptionEqualToValue={(a, b) => a.id === b.id} onChange={(_, a) => setAmbiente(a?.id ?? null)}
          noOptionsText="Nenhum ambiente com esse nome" loadingText="Carregando…" loading={ambientes.length === 0}
          renderInput={p => <TextField {...p} placeholder="Todos os ambientes" aria-label="Ambiente" />}
        />
        <select className="ng-select" value={tipo} onChange={e => setTipo(e.target.value)} aria-label="Tipo">
          <option value="">Power BI e HTML</option><option value="powerbi">Power BI</option><option value="html">Relatório HTML</option>
        </select>
        <select className="ng-select" value={situacao} onChange={e => setSituacao(e.target.value)} aria-label="Situação">
          <option value="">Ativos e inativos</option><option value="ativo">Só ativos</option><option value="inativo">Só inativos</option>
        </select>
        <select className="ng-select" value={dataset} onChange={e => setDataset(e.target.value)} aria-label="Dataset">
          <option value="">Com e sem dataset</option><option value="sem">Sem dataset</option><option value="com">Com dataset</option>
        </select>
      </div>

      <div className="ng-cartao" style={{ padding: '6px 10px' }}>
        <div className="ng-tabela">
          <table>
            <thead><tr><th>Dashboard</th><th>Tipo</th><th className="n">Ambientes</th></tr></thead>
            <tbody>
              {erro && <tr><td colSpan={3} className="vazio">{erro}</td></tr>}
              {!lista && !erro && <tr><td colSpan={3} className="vazio">Carregando o catálogo…</td></tr>}
              {lista && visiveis.length === 0 && <tr><td colSpan={3} className="vazio">Nenhum dashboard com esses filtros.</td></tr>}
              {visiveis.map(d => (
                <tr key={d.id} className="clicavel" onClick={() => navigate(`/app/gestao/dashboards/${d.id}`)}>
                  <td><b>{d.nome}</b>{!d.ativo && <> <span className="ng-pill inativo">Inativo</span></>}{d.tipo !== 'html' && !d.temDataset && <> <span className="ng-pill alerta">Sem dataset</span></>}</td>
                  <td>{d.tipo === 'html' ? 'Relatório HTML' : 'Power BI'}</td>
                  <td className="n">{d.ambientes.length}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtrada.length > visiveis.length && (
          <div style={{ textAlign: 'center', padding: 10 }}>
            <button type="button" className="ng-btn" onClick={() => setPagina(p => p + 1)}>Mostrar mais ({filtrada.length - visiveis.length})</button>
          </div>
        )}
      </div>

      <NovoDashboard aberto={novo} aoFechar={() => setNovo(false)} />
    </div>
  )
}
